import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlassPlus } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlassPlus';
import type { PublicAssetShare } from 'fym-shared/src/music24/assetShares';
import { TypedMemo } from '../../common/types';
import { getFullAPIFileURL } from '../../common/getFullAPIFileURL';
import { isTallShare, shouldLoopShareVideo } from './assetShareFacts';
import { assetShareMessages } from './assetShareMessages';
import { AssetShareZoom } from './AssetShareZoom';
import { useShareMediaLabel } from './useShareMediaLabel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the share page; each part uses its own slice of it */
import styles from './AssetShare.module.scss';
/* eslint-enable css-modules/no-unused-class */

function frameStyleOf(share: PublicAssetShare): React.CSSProperties | undefined {
  if (share.width === null || share.height === null || share.width <= 0 || share.height <= 0) {
    return undefined;
  }
  return { aspectRatio: `${share.width} / ${share.height}` };
}

/**
 * The player of the theater page (design asset-share c1-c5): as large as the window allows (at most
 * 1040 px wide, at most 72 vh high), edge to edge on a phone. A video has the controls of the
 * browser with no download item, and the poster. A short loop (a Canvas loop, a motion artwork)
 * plays muted and loops at once. A still picture has a zoom button that shows it full size. The file
 * comes from the same-origin `/api_api/` path, which answers Range requests. The page has no
 * Download action (owner decision 2026-10-19).
 */
const AssetShareMediaComponent: React.FC<{ share: PublicAssetShare }> = ({ share }) => {
  const intl = useIntl();
  const src = getFullAPIFileURL(share.mediaUrl);
  const label = useShareMediaLabel(share);
  const frameStyle = useMemo(() => frameStyleOf(share), [share]);
  const frameClass = clsx(
    styles.frame,
    share.mediaKind === 'image' && styles.framePicture,
    share.width !== null && share.height !== null && isTallShare(share) && styles.frameTall
  );
  const width = share.width ?? undefined;
  const height = share.height ?? undefined;
  const [zoomed, setZoomed] = useState(false);
  const openZoom = useCallback(() => setZoomed(true), []);
  const closeZoom = useCallback(() => setZoomed(false), []);

  if (share.mediaKind === 'image') {
    return (
      <div className={styles.stage}>
        <div className={frameClass} style={frameStyle}>
          <img className={styles.file} src={src} alt={label} width={width} height={height} />
          <button
            type="button"
            className={clsx(styles.glassButton, styles.zoomButton)}
            onClick={openZoom}
            aria-label={intl.formatMessage(assetShareMessages.zoom)}
            data-testid="asset-share-zoom"
          >
            <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
          </button>
        </div>
        {zoomed && <AssetShareZoom src={src} alt={label} onClose={closeZoom} />}
      </div>
    );
  }

  const loop = shouldLoopShareVideo(share);
  return (
    <div className={styles.stage}>
      <div className={frameClass} style={frameStyle}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a music video or reel: its captions are burnt in */}
        <video
          className={styles.file}
          controls
          controlsList="nodownload"
          playsInline
          preload="metadata"
          loop={loop}
          autoPlay={loop}
          muted={loop}
          poster={share.posterUrl !== null ? getFullAPIFileURL(share.posterUrl) : undefined}
          width={width}
          height={height}
          aria-label={label}
          data-testid="asset-share-video"
        >
          <source src={src} type={share.contentType} />
          {intl.formatMessage(assetShareMessages.videoFallback)}
        </video>
      </div>
    </div>
  );
};

export const AssetShareMedia = TypedMemo(AssetShareMediaComponent);
